<script setup lang="ts">
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
import ArrowLeft from '@/shared/assets/icons/ArrowLeft.svg';
import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
import CellIcon from '@/shared/assets/icons/CellIcon.svg';
import CheckboxIcon from '@/shared/assets/icons/CheckboxIcon.svg';
import ColumnHorizontalIcon from '@/shared/assets/icons/ColumnHorizontalIcon.svg';
import ConfigsIcon from '@/shared/assets/icons/ConfigIcon.svg?component';
import FontIcon from '@/shared/assets/icons/FontIcon.svg';
import TextAlignJustifyIcon from '@/shared/assets/icons/TextAlignJustifyIcon.svg'
import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
import MessageIcon from '@/shared/assets/icons/MessageIcon.svg';
import UserIcon from '@/shared/assets/icons/UserIcon.svg';
import PinIcon from '@/shared/assets/icons/PinIcon.svg';
import BookmarkIcon from '@/shared/assets/icons/BookmarkIcon.svg';
import NumberIcon from '@/shared/assets/icons/NumberIcon.svg';
import RadioIcon from '@/shared/assets/icons/RadioIcon.svg';
import TableIcon from '@/shared/assets/icons/TableIcon.svg';
import TextIcon from '@/shared/assets/icons/TextIcon.svg';
import { Accordion, AccordionContent, AccordionItem, IconImage, Tooltip } from '@/shared/ui';
import { Palette, Plus, Trash2 } from 'lucide-vue-next';
import { computed } from 'vue';
import {
  removeFormBuilderBodyTable,
  removeFormBuilderCanvasElement,
  removeFormBuilderTableCellElement,
} from '../../composables/removeFormBuilderCanvasElement';
import { useFormBuilderSidebars } from '../../composables/useFormBuilderSidebars';
import { useRuleAwareElementDeletion } from '../../composables/useRuleAwareElementDeletion';
import { DESIGN_COMPONENT, getElementSectionConfig } from '../../lib/element-section-config';
import { useFormBuilderStore } from '../../model/form-builder.store';
import type { ElementType, TableProperties as TablePropertiesType } from '../../model/form.types';
import { useTableEditorStore } from '../../model/table-editor.store';
import { usePropertyAccordions } from '../../model/usePropertyAccordions';
import { CellProperties, TableProperties } from '../elements/properties';
import PropertyAccordionTrigger from './PropertyAccordionTrigger.vue';
import PropertySectionHeader from './PropertySectionHeader.vue';

  const { rightCollapsed: isCollapsed, toggleRight: toggleSidebar } = useFormBuilderSidebars();

  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();
  const { requestRuleAwareDeletion } = useRuleAwareElementDeletion();
  const accordionState = usePropertyAccordions() as unknown as {
    accordionValues: ReturnType<typeof computed<string[]>>;
    handleAccordionChange: (values: string[]) => void;
  };
  const accordionValues = accordionState.accordionValues;
  const handleAccordionChange = accordionState.handleAccordionChange;

  const selectedElement = computed(() => formBuilderStore.selectedElement);
  const isSelectedHeaderElement = computed(
    () =>
      selectedElement.value?.type === 'HEADER' ||
      (selectedElement.value?.type === 'TABLE' &&
        (selectedElement.value?.properties as TablePropertiesType)?.isHeader === true)
  );
  const selectedCell = computed(() => tableEditorStore.selectedCell);
  const selectedCellData = computed(() => tableEditorStore.selectedCellData);
  const hasSelectedCells = computed(() => tableEditorStore.selectedCells.length > 0);
  const isEditingMode = computed(() => tableEditorStore.isEditingMode);
  const activeTable = computed(() => tableEditorStore.activeTable);

  const getElementLabel = (type: ElementType): string => {
    const labels: Record<string, string> = {
      HEADER: 'Cabeçalho',
      FIXED_TEXT: 'Texto Fixo',
      TEXT_INPUT: 'Campo de Texto',
      PARAGRAPH: 'Parágrafo',
      NUMBER_INPUT: 'Número',
      RADIO_GROUP: 'Seleção radial',
      DATE_INPUT: 'Data',
      URL_INPUT: 'URL',
      EMAIL_INPUT: 'E-mail',
      REGISTER_NUMBER_INPUT: 'Matrícula',
      INDEXER_TITLE: 'Título indexador',
      FILL_TITLE: 'Título Preenchimento',
      DROPDOWN: 'Dropdown',
      TABLE: 'Tabela',
      IMAGE: 'Imagem',
      CHECKBOX: 'Checkbox',
    };
    return labels[type] ?? 'Elemento';
  };

  const elementSectionConfig = computed(() => {
    if (!selectedElement.value) return null;
    if (isSelectedHeaderElement.value) return getElementSectionConfig('HEADER');
    if (selectedElement.value.type !== 'TABLE') {
      return getElementSectionConfig(selectedElement.value.type);
    }
    return null;
  });

  const cellSectionConfig = computed(() => {
    if (selectedCellData.value?.content) {
      return getElementSectionConfig(selectedCellData.value.content.type as ElementType);
    }
    return null;
  });

  const showTableAccordion = computed(
    () =>
      selectedElement.value?.type === 'TABLE' &&
      (selectedElement.value?.properties as TablePropertiesType)?.isHeader !== true
  );
  const showCellAccordion = computed(() => {
    return activeTable.value !== null && isEditingMode.value && (hasSelectedCells.value || selectedCell.value);
  });
  const elementAccordionLabel = computed(() => {
    if (selectedCellData.value?.content) {
      return getElementLabel(selectedCellData.value.content.type);
    }
    if (selectedElement.value) {
      return getElementLabel(selectedElement.value.type);
    }
    return 'Elemento';
  });

  const showElementAccordion = computed(() => {
    return (
      (selectedElement.value &&
        (selectedElement.value.type !== 'TABLE' || isSelectedHeaderElement.value)) ||
      (activeTable.value !== null && selectedCellData.value?.content !== undefined)
    );
  });

  const currentElementId = computed(() => {
    if (selectedCellData.value?.content) return selectedCellData.value.content.id;
    return selectedElement.value?.id ?? null;
  });

  const currentElementType = computed(() => {
    if (selectedCellData.value?.content) return selectedCellData.value.content.type as ElementType;
    if (isSelectedHeaderElement.value) return 'HEADER';
    return selectedElement.value?.type ?? null;
  });

  const ELEMENT_ICON_MAP: Record<ElementType, typeof TableIcon> = {
    TABLE: TableIcon,
    HEADER: ColumnHorizontalIcon,
    FIXED_TEXT: TextIcon,
    TEXT_INPUT: FontIcon,
    PARAGRAPH: TextAlignJustifyIcon,
    NUMBER_INPUT: NumberIcon,
    DROPDOWN: ArrowDown,
    CHECKBOX: CheckboxIcon,
    RADIO_GROUP: RadioIcon,
    DATE_INPUT: CalendarIcon,
    EMAIL_INPUT: MessageIcon,
    URL_INPUT: LinkIcon,
    REGISTER_NUMBER_INPUT: UserIcon,
    INDEXER_TITLE: PinIcon,
    FILL_TITLE: BookmarkIcon,
    IMAGE: TextIcon,
  };

  const currentElementIcon = computed(() => {
    const type = currentElementType.value;
    if (!type) return null;
    return ELEMENT_ICON_MAP[type];
  });

  const currentSectionConfig = computed(() => {
    return cellSectionConfig.value ?? elementSectionConfig.value;
  });

  function handleDeleteElement() {
    if (!currentElementId.value) return;
    const cellContent = selectedCellData.value?.content;
    if (
      cellContent &&
      requestRuleAwareDeletion(cellContent, () => removeFormBuilderTableCellElement(false))
    ) {
      return;
    }
    if (removeFormBuilderTableCellElement()) return;
    const element = selectedElement.value;
    if (
      element &&
      requestRuleAwareDeletion(element, () => {
        if (removeFormBuilderBodyTable(element.id)) return;
        removeFormBuilderCanvasElement(element.id, false);
      })
    ) {
      return;
    }
    if (removeFormBuilderBodyTable(currentElementId.value)) return;
    removeFormBuilderCanvasElement(currentElementId.value);
  }

  const onAccordionValueChange = (values: string | string[] | undefined) => {
    if (Array.isArray(values)) {
      handleAccordionChange(values);
    } else {
      handleAccordionChange([]);
    }
  };
</script>

<template>
  <div class="relative h-full" data-form-builder-preserve-selection>
    <Tooltip
      v-if="isCollapsed"
      content="Propriedades"
      placement="left"
      class="absolute top-4 -left-[58px]"
    >
      <button
        class="h-[42px] w-[42px] border border-[var(--color-border-color)] rounded-lg flex items-center justify-center bg-white-01 hover:bg-[var(--hover-primary-color)] transition-colors"
        @click="toggleSidebar"
      >
        <IconImage
          :src="ArrowLeft"
          class="h-6 w-6"
          alt="Expandir menu"
        />
      </button>
    </Tooltip>

    <aside
      data-testid="property-editor-sidebar"
      data-property-sidebar
      :class="[
        'h-full flex flex-col transition-all duration-300',
        isCollapsed
          ? 'w-0 overflow-hidden'
          : 'w-[320px] overflow-y-auto bg-[var(--color-white-02)] border-l border-[var(--color-gray-02)]',
      ]"
      @click.stop
    >
      <div class="p-4 flex items-center justify-start border-b border-[var(--color-gray-02)]">
        <button
          class="h-[42px] w-[42px] border mr-4 border-[var(--color-border-color)] rounded-lg flex items-center justify-center
          hover:bg-[var(--hover-primary-color)] transition-colors"
          @click="toggleSidebar"
        >
          <IconImage
            :src="ArrowRight"
            class="h-6 w-6"
            alt="Colapsar menu"
          />
        </button>
        <h2 class="text-sm font-semibold text-[var(--color-gray-07)]">Propriedades</h2>
      </div>

      <div v-if="!selectedElement && !isEditingMode" class="flex-1 flex flex-col py-6 px-4">
        <p class="text-xs text-gray-500 mx-4">
          Nenhum campo selecionado no momento
        </p>
      </div>

      <div
        v-else-if="showTableAccordion || showCellAccordion || showElementAccordion"
        class="flex-1 px-4 py-6"
      >
        <Accordion
          type="multiple"
          :model-value="accordionValues"
          @update:model-value="onAccordionValueChange"
          class="space-y-2"
        >
          <AccordionItem v-if="showTableAccordion" value="table" class="border-none">
            <PropertyAccordionTrigger :icon="TableIcon">
              Tabela
            </PropertyAccordionTrigger>
            <AccordionContent class="pt-4 pb-0">
              <TableProperties
                v-if="selectedElement"
                :element-id="selectedElement.id"
                :key="selectedElement.id"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem v-if="showCellAccordion" value="cell" class="border-none">
            <PropertyAccordionTrigger :icon="CellIcon">
              Configurações da célula
            </PropertyAccordionTrigger>
            <AccordionContent class="pt-4 pb-0">
              <div v-if="hasSelectedCells">
                <PropertySectionHeader :icon="Palette" title="Design" />
                <CellProperties />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            v-if="showElementAccordion && currentSectionConfig && currentElementId && currentElementType"
            value="element"
            class="border-none"
          >
            <PropertyAccordionTrigger :icon="currentElementIcon ?? undefined">
              {{ elementAccordionLabel }}
            </PropertyAccordionTrigger>
            <AccordionContent class="pt-4 pb-0">
              <div class="flex flex-col">
                <div v-if="currentSectionConfig.sections.includes('config')">
                  <PropertySectionHeader :icon="ConfigsIcon" title="Configurações" icon-class="size-6" />
                  <component
                    :is="currentSectionConfig.configComponent"
                    :element-id="currentElementId"
                    :key="`config-${currentElementId}`"
                  />
                </div>

                <div v-if="currentSectionConfig.sections.includes('design')" class="mt-4">
                  <PropertySectionHeader :icon="Palette" title="Design" />
                  <component
                    :is="currentSectionConfig?.designComponent ?? DESIGN_COMPONENT"
                    :element-id="currentElementId"
                    :key="`design-${currentElementId}`"
                  />
                </div>

                <div v-if="currentSectionConfig.sections.includes('extras')" class="mt-4">
                  <PropertySectionHeader :icon="Plus" title="Opções extras" />
                  <component
                    v-if="currentSectionConfig.extrasComponent"
                    :is="currentSectionConfig.extrasComponent"
                    :element-id="currentElementId"
                    :key="`extras-${currentElementId}`"
                    section="extras"
                  />
                  <div v-else class="py-4">
                    <p class="text-xs text-gray-400">
                      Em breve
                    </p>
                  </div>
                </div>

                <button
                  v-if="!isSelectedHeaderElement"
                  type="button"
                  class="mt-4 flex items-center justify-center gap-2 w-full h-[42px] rounded-lg border border-gray-02 bg-white-01 text-sm font-medium text-gray-07 hover:bg-gray-02 transition-colors"
                  @click="handleDeleteElement"
                >
                  <Trash2 class="size-5 text-[var(--color-red-16)]" />
                  Deletar
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </aside>
  </div>
</template>
